/* Editor — All Manuscripts (Sep-2026, extended Oct-2026).
 *
 * Every genuine Loom AI-written novel (see routes_editor_manuscripts.py
 * for the `user_id == loom_studio_ai` scoping that keeps test fixtures
 * and customers' private drafts out of this list). Search by title,
 * filter by genre/pseudonym, then either "Open" a single novel into
 * the Editor Review Workspace, or select several and run one AI pass
 * (evaluate or rewrite) across all of them at once.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Search, BookOpen, ArrowRight, Sparkles, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API  = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const MODEL_OPTIONS = [
  { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5' },
  { value: 'claude-opus-4-7',            label: 'Claude Opus 4.7' },
];

const STATUS_BADGE = {
  generated: { label: 'Generated', className: 'bg-emerald-500/15 text-emerald-600' },
  queued:    { label: 'Queued',    className: 'bg-amber-500/15 text-amber-600' },
};

export default function EditorAllManuscriptsPanel() {
  const [rows,       setRows]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [q,          setQ]          = useState('');
  const [genre,      setGenre]      = useState('');
  const [pseudonym,  setPseudonym]  = useState('');
  const [filters,    setFilters]    = useState({ genres: [], pseudonyms: [] });
  const [loading,    setLoading]    = useState(true);
  const [opening,    setOpening]    = useState(null);
  const [selected,   setSelected]   = useState(() => new Set());
  const [batchOpen,  setBatchOpen]  = useState(false);
  const [batchMode,  setBatchMode]  = useState('evaluate');
  const [batchModel, setBatchModel] = useState(MODEL_OPTIONS[0].value);
  const [batchBusy,  setBatchBusy]  = useState(false);
  const [batchResults, setBatchResults] = useState(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    // StrictMode double-invokes effects on mount (mount->cleanup->mount)
    // in dev; without this, the cleanup's `false` from the first pass
    // would stick forever and silently kill every poll before it starts.
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const navigate = useNavigate();

  const load = useCallback(async (params) => {
    setLoading(true);
    try {
      const r = await http.get('/editor/manuscripts', {
        params: { q: params.q || undefined, genre: params.genre || undefined,
                   pseudonym: params.pseudonym || undefined, limit: 100 },
      });
      setRows(r.data?.manuscripts || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load manuscripts.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load({ q, genre, pseudonym }); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    http.get('/editor/manuscripts/filters')
      .then((r) => setFilters(r.data || { genres: [], pseudonyms: [] }))
      .catch(() => {});
  }, []);

  const applyFilters = (e) => { e?.preventDefault?.(); load({ q, genre, pseudonym }); setSelected(new Set()); };

  const openManuscript = async (projectId) => {
    setOpening(projectId);
    try {
      const r = await http.post(`/editor/manuscripts/${projectId}/open`);
      navigate(`/editor/manuscript/${r.data.review_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open manuscript.');
    } finally { setOpening(null); }
  };

  const toggleSelected = (projectId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId); else next.add(projectId);
      return next;
    });
  };

  const [batchProgress, setBatchProgress] = useState(null); // {current, total}

  // 2026-10 — Batch AI Polish now runs as a background job: each
  // selected manuscript can itself take minutes (full-text, chunked
  // into ~9000-word blocks run sequentially), which used to blow past
  // the ingress's ~100s request timeout and come back as a bare 502
  // on anything beyond one small manuscript. Poll for progress instead.
  const pollBatchJob = (jobId) => {
    const poll = async () => {
      if (!mountedRef.current) return;
      try {
        const r = await http.get(`/editor/manuscripts/batch-ai-assist-job/${jobId}`);
        if (!mountedRef.current) return;
        const job = r.data;
        setBatchProgress({
          current: job.current_index, total: job.total_manuscripts,
          title: job.current_title, block: job.current_block, totalBlocks: job.current_total_blocks,
        });
        setBatchResults(job.results || []);
        if (job.status === 'running') {
          setTimeout(poll, 2500);
          return;
        }
        setBatchBusy(false);
        const okCount = (job.results || []).filter((x) => x.ok).length;
        if (job.status === 'error' || job.status === 'stalled') {
          toast.error(job.error || 'Batch AI assist failed.');
        } else {
          toast.success(`AI ${batchMode} finished on ${okCount}/${job.total_manuscripts} manuscripts.`);
        }
      } catch (e) {
        if (!mountedRef.current) return;
        setBatchBusy(false);
        toast.error('Could not reach the batch AI assist service.');
      }
    };
    poll();
  };

  const runBatch = async () => {
    setBatchBusy(true);
    setBatchResults(null);
    setBatchProgress(null);
    try {
      const r = await http.post('/editor/manuscripts/batch-ai-assist', {
        project_ids: Array.from(selected),
        mode: batchMode,
        model: batchModel,
      });
      pollBatchJob(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Batch AI assist failed.');
      setBatchBusy(false);
    }
  };

  return (
    <section data-testid="editor-all-manuscripts-panel" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[13px] font-semibold">All manuscripts</div>
          <div className="text-[10.5px] text-muted-foreground">{total} AI-written novels — open any of them to evaluate and adjust for publication.</div>
          {total > rows.length && (
            <div className="text-[10.5px] font-medium text-amber-600" data-testid="editor-manuscripts-truncated-notice">
              Showing the first {rows.length} of {total} — narrow with search or a filter to see the rest.
            </div>
          )}
        </div>
        <form onSubmit={applyFilters} className="flex flex-wrap items-center gap-2">
          <div className="relative w-full max-w-xs sm:w-56">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search by title…"
                   data-testid="editor-manuscripts-search"
                   className="h-8 w-full rounded-md border border-border bg-background pl-7 pr-2 text-[12px] outline-none focus:border-primary" />
          </div>
          <select value={genre} onChange={(e) => setGenre(e.target.value)}
                  data-testid="editor-manuscripts-genre-filter"
                  className="h-8 rounded-md border border-border bg-background px-2 text-[12px] outline-none focus:border-primary">
            <option value="">All genres</option>
            {filters.genres.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <select value={pseudonym} onChange={(e) => setPseudonym(e.target.value)}
                  data-testid="editor-manuscripts-pseudonym-filter"
                  className="h-8 rounded-md border border-border bg-background px-2 text-[12px] outline-none focus:border-primary">
            <option value="">All authors</option>
            {filters.pseudonyms.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <button type="submit" data-testid="editor-manuscripts-apply-filters"
                  className="h-8 rounded-md border border-border px-2.5 text-[12px] hover:bg-secondary">
            Filter
          </button>
        </form>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center justify-between rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-[12px]" data-testid="editor-manuscripts-batch-bar">
          <span>{selected.size} selected</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelected(new Set())} data-testid="editor-manuscripts-clear-selection"
                    className="rounded-md border border-border px-2 py-1 text-[11px] hover:bg-secondary">
              Clear
            </button>
            <button onClick={() => setBatchOpen(true)} data-testid="editor-manuscripts-batch-open"
                    className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground hover:bg-primary/90">
              <Sparkles className="h-3 w-3" /> Run AI Assist on {selected.size}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex h-40 items-center justify-center text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading manuscripts…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-6 text-center text-[12px] text-muted-foreground">
          No manuscripts match “{q}”.
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-border">
          <table className="w-full text-[12px]">
            <thead className="bg-panel-header/40 text-[10.5px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="w-8 px-3 py-2" />
                <th className="px-3 py-2 text-left">Title</th>
                <th className="px-3 py-2 text-left">Author</th>
                <th className="px-3 py-2 text-left">Genre</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Chapters</th>
                <th className="px-3 py-2 text-right">Words</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => {
                const badge = STATUS_BADGE[m.gen_status] || STATUS_BADGE.queued;
                return (
                  <tr key={m.project_id} data-testid={`editor-manuscript-row-${m.project_id}`}
                      className="border-t border-border hover:bg-secondary/40">
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={selected.has(m.project_id)}
                             onChange={() => toggleSelected(m.project_id)}
                             data-testid={`editor-manuscript-select-${m.project_id}`} />
                    </td>
                    <td className="px-3 py-2 font-semibold">
                      <span className="inline-flex items-center gap-1.5">
                        <BookOpen className="h-3.5 w-3.5 text-muted-foreground" /> {m.title}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{m.author_name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{m.genre || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-medium ${badge.className}`}
                            data-testid={`editor-manuscript-status-${m.project_id}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">{m.chapter_count}</td>
                    <td className="px-3 py-2 text-right">{(m.word_count || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => openManuscript(m.project_id)} disabled={opening === m.project_id}
                              data-testid={`editor-manuscript-open-${m.project_id}`}
                              className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                        {opening === m.project_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRight className="h-3 w-3" />}
                        {m.open_review_id ? 'Resume' : 'Open'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {batchOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="editor-manuscripts-batch-dialog">
          <div className="w-full max-w-md rounded-lg border border-border bg-background p-4 shadow-lg">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-[13px] font-semibold">Run AI Assist on {selected.size} manuscripts</div>
              <button onClick={() => { setBatchOpen(false); if (batchResults) setSelected(new Set()); setBatchResults(null); }} data-testid="editor-manuscripts-batch-close">
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>

            {!batchResults && !batchBusy ? (
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-[11px] text-muted-foreground">Action</label>
                  <select value={batchMode} onChange={(e) => setBatchMode(e.target.value)}
                          data-testid="editor-manuscripts-batch-mode"
                          className="h-8 w-full rounded-md border border-border bg-background px-2 text-[12px]">
                    <option value="evaluate">Evaluate (editorial notes)</option>
                    <option value="rewrite">Rewrite (line-edit pass)</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] text-muted-foreground">Model</label>
                  <select value={batchModel} onChange={(e) => setBatchModel(e.target.value)}
                          data-testid="editor-manuscripts-batch-model"
                          className="h-8 w-full rounded-md border border-border bg-background px-2 text-[12px]">
                    {MODEL_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <button onClick={runBatch} disabled={batchBusy} data-testid="editor-manuscripts-batch-run"
                        className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                  {batchBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  {batchBusy ? 'Running…' : 'Run'}
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {batchBusy && (
                  <div className="space-y-0.5 text-[11px] text-muted-foreground" data-testid="editor-manuscripts-batch-progress">
                    <div className="flex items-center gap-1.5">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Processing manuscript {Math.min((batchProgress?.current || 0) + 1, batchProgress?.total || selected.size)} of {batchProgress?.total || selected.size}
                      {batchProgress?.title ? ` — ${batchProgress.title}` : ''}
                    </div>
                    {batchProgress?.block && batchProgress?.totalBlocks > 1 && (
                      <div className="pl-[18px] text-[10.5px] font-semibold text-primary" data-testid="editor-manuscripts-batch-block-progress">
                        Block {batchProgress.block} of {batchProgress.totalBlocks}…
                      </div>
                    )}
                  </div>
                )}
                <div className="max-h-64 space-y-1 overflow-y-auto">
                  {(batchResults || []).map((r) => (
                    <div key={r.project_id} data-testid={`editor-manuscripts-batch-result-${r.project_id}`}
                         className={`flex items-center justify-between rounded-md border px-2 py-1.5 text-[11.5px] ${r.ok ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-destructive/30 bg-destructive/5'}`}>
                      <span className="truncate">{r.title || `Untitled manuscript (${r.project_id})`}</span>
                      <span className={r.ok ? 'text-emerald-600' : 'text-destructive'}>{r.ok ? 'Done' : r.error}</span>
                    </div>
                  ))}
                </div>
                {!batchBusy && (
                  <button onClick={() => { setBatchOpen(false); setBatchResults(null); setSelected(new Set()); }}
                          data-testid="editor-manuscripts-batch-done"
                          className="w-full rounded-md border border-border px-3 py-1.5 text-[12px] hover:bg-secondary">
                    Close
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
